import { gql } from 'graphql-tag';
import { PermissionType } from '@watershed/constants/permissions';

import {
  GQClimateProgramTimelineProjectFieldsFragment,
  GQLinkedMeasurementFieldsFragment,
} from '@watershed/app-dashboard/generated/graphql-operations';
import { GQClimateProgramTimelineProjectObjectType } from '@watershed/app-dashboard/generated/graphql-schema-types';

import { Fade, Stack } from '@mui/material';
import {
  OnboardingNavCard,
  StrategizeNavCard,
  StartNewMeasureNavCard,
  ActiveMeasureNavCard,
} from './HomeNavCards';
import { getGqlResultDataBang } from '@watershed/shared-frontend/utils/errorUtils';
import isFetchingOrStale from '@watershed/shared-frontend/utils/isFetchingOrStale';
import { useOnboardingHomePageQuery } from '@watershed/app-dashboard/generated/urql';

import { LinkedMeasurementTimelineProject } from '../climate-program/CtaHelpers';
import LoadingPage from '@watershed/shared-frontend/components/LoadingPage';
import useHasPermission from '../../utils/useHasPermission';

gql`
  fragment ActiveMeasurementProjectForOnboardingHomePage on MeasurementProject {
    id
    name
    measurementTasks {
      ...MeasurementTaskForMeasurementPage
    }
    footprintStatus
  }

  fragment OrganizationForOnboardingHomePage on Organization {
    id
    users {
      edges {
        node {
          id
        }
      }
    }
    hasActiveWorkosConnection
  }

  query OnboardingHomePage @withOwner(owner: EnterpriseFoundations) {
    climateProgramTimelineProjects {
      ...ClimateProgramTimelineProjectFields
    }
    activeMeasurementProjects {
      ...ActiveMeasurementProjectForOnboardingHomePage
    }
  }

  query OnboardingNavCard @withOwner(owner: EnterpriseFoundations) {
    organization {
      ...OrganizationForOnboardingHomePage
    }
  }
`;

export function OnboardingHomePage() {
  const isAdmin = useHasPermission(PermissionType.Admin);
  const [result] = useOnboardingHomePageQuery();
  if (isFetchingOrStale(result)) {
    return <LoadingPage />;
  }
  const data = getGqlResultDataBang(result);
  const { climateProgramTimelineProjects, activeMeasurementProjects } = data;
  const measurementTimelineProjects: Array<
    GQClimateProgramTimelineProjectFieldsFragment & {
      object: GQLinkedMeasurementFieldsFragment;
    }
  > = climateProgramTimelineProjects.filter(
    (cptp): cptp is LinkedMeasurementTimelineProject =>
      cptp.object.__typename ===
      GQClimateProgramTimelineProjectObjectType.MeasurementProject
  );
  const hasMeasurementProjects = measurementTimelineProjects.length > 0;
  const hasActiveMeasurementProjects = activeMeasurementProjects.length > 0;
  const showNewMeasureNavCard =
    hasMeasurementProjects && !hasActiveMeasurementProjects;
  return (
    <Fade in>
      <Stack
        gap={2}
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: (theme) => theme.palette.info.light,
        }}
      >
        {!hasMeasurementProjects && isAdmin && <OnboardingNavCard />}
        {showNewMeasureNavCard && <StartNewMeasureNavCard />}
        {activeMeasurementProjects.map((activeMeasurementProject) => (
          <ActiveMeasureNavCard
            key={activeMeasurementProject.id}
            measurementProject={activeMeasurementProject}
          />
        ))}
        <StrategizeNavCard
          climateProgramTimelineProjects={climateProgramTimelineProjects}
        />
      </Stack>
    </Fade>
  );
}
